import {
  Group,
  Skeleton,
  Table,
  Title,
  Text,
  Paper,
  Stack,
  Flex,
  Button,
  AppShell,
  Loader,
} from "@mantine/core";
import {
  GetGuestsResponseRecord,
  useGetGuestsQuery,
} from "../slices/api/guestsApi";
import { JSX } from "react";
import { useLocation } from "wouter";
import { formatFullName } from "../shared/utility";
import {
  formatGuestStatus,
  GetAssignment,
  GetTotalForActivity,
  GuestStatus,
  Activity,
} from "../slices/types";
import { useGetVentQuery } from "../slices/api/ventsApi";
import { useGetAssignmentsQuery } from "../slices/api/assignmentsApi";
import { IconRsvp } from "../shared/components/Icons";
import { handleFetchingError } from "../shared/ErrorMessage";
import { EbTooltip } from "../shared/components/EbTooltip";
import { EbActionIcon } from "../shared/components/EbActionItem";

interface Props {
  activityId: string;
  ventId: string;
  onClose: () => void;
}
function ActivityAssignments(props: Props) {
  const ventId = props.ventId;
  const [_, navigate] = useLocation();

  const {
    data: guestsResponse,
    isFetching: isFetchingGuests,
    isSuccess: isSuccessGuests,
    isError: isErrorFetchingGuests,
    error: guestsError,
  } = useGetGuestsQuery({
    ventId: ventId,
  });
  const {
    data: ventResponse,
    isSuccess: isSuccessVent,
    isFetching: isFetchingVent,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({
    id: ventId,
  });
  const {
    data: assignmentsResponse,
    isFetching: isFetchingAssignments,
    isSuccess: isSuccessAssignments,
    isError: isErrorFetchingAssignments,
    error: assignmentsError,
  } = useGetAssignmentsQuery({
    ventId: ventId,
  });

  const isFetching =
    isFetchingGuests || isFetchingAssignments || isFetchingVent;
  const isSuccess = isSuccessGuests || isSuccessAssignments || isSuccessVent;

  interface ActivityAssignment {
    guestId: string;
    guestName: string;
    quantity: number;
    comment: string;
    guestStatus: GuestStatus;
  }

  if (
    isErrorFetchingAssignments ||
    isErrorFetchingVent ||
    isErrorFetchingGuests
  ) {
    handleFetchingError(assignmentsError ?? guestsError ?? ventError);
    return null;
  }

  if (!assignmentsResponse || !guestsResponse || !ventResponse) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  // Make a dictionary of guests by guestId
  const guests: Record<string, GetGuestsResponseRecord> = {};
  guestsResponse.guests.forEach((record) => {
    guests[record.id] = record;
  });

  let assignmentRows: JSX.Element[] = [];
  let totalAssigned = 0;
  let activity: Activity = {} as Activity;
  const allAssignments = assignmentsResponse.assignments;
  if (isSuccessVent && isSuccessAssignments && isSuccessGuests) {
    totalAssigned = GetTotalForActivity(allAssignments, props.activityId);
    activity = ventResponse.activities[props.activityId];

    // Need one row per guest assigned to this activity
    const activityAssignments: ActivityAssignment[] = [];
    for (const guestResponseRecord of guestsResponse.guests) {
      const { id: guestId, firstName, lastName } = guestResponseRecord;
      const guestAssignment = GetAssignment(
        allAssignments,
        guestId,
        props.activityId,
      );
      if (guestAssignment.quantity == 0) continue;
      const activityAssignment: ActivityAssignment = {
        guestId,
        guestName: formatFullName(firstName, lastName),
        quantity: guestAssignment.quantity,
        comment: guestAssignment.comment,
        guestStatus: guestResponseRecord.status,
      };
      activityAssignments.push(activityAssignment);
    }

    const sorted = activityAssignments.sort((a, b) => {
      return a.guestName < b.guestName ? -1 : 1;
    });
    assignmentRows = sorted.map((assignment) => (
      <Table.Tr key={assignment.guestId}>
        <Table.Td>
          <Group>
            <EbActionIcon
              disabled={
                !(
                  ventResponse.canUserViewConfig ||
                  guests[assignment.guestId].canUserEdit
                )
              }
              aria-label="RSVP"
              onClick={() => {
                props.onClose();
                navigate(
                  `/console/guest/event/${ventId}/guest/${assignment.guestId}`,
                );
              }}
            >
              <EbTooltip label="RSVP">
                <IconRsvp />
              </EbTooltip>
            </EbActionIcon>
          </Group>
        </Table.Td>
        <Table.Td>{assignment.guestName}</Table.Td>
        <Table.Td>{assignment.quantity}</Table.Td>
        <Table.Td>{assignment.comment}</Table.Td>
        <Table.Td>{formatGuestStatus(assignment.guestStatus)}</Table.Td>
      </Table.Tr>
    ));
  }
  return (
    <Paper>
      <Stack>
        <Title order={3}>Assignments for {activity.description}</Title>
        <Text>
          Total assigned: {totalAssigned}, Limit per guest:{" "}
          {activity.limitPerGuest}
          {activity.limitPerEvent > 0 && (
            <span>, Remaining: {activity.limitPerEvent - totalAssigned}</span>
          )}
        </Text>
      </Stack>
      <Skeleton visible={isFetching}>
        <div hidden={isSuccess === false}>
          <Table stickyHeader stickyHeaderOffset={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Actions</Table.Th>
                <Table.Th>Guest Name</Table.Th>
                <Table.Th>Assigned</Table.Th>
                <Table.Th>Comment</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{assignmentRows}</Table.Tbody>
          </Table>
        </div>
        <div hidden={isFetching || assignmentRows.length > 0}>
          No assignments have been made for this activity.
        </div>
      </Skeleton>
      <Flex justify="flex-end">
        <Button mt="md" mr="md" onClick={props.onClose}>
          Close
        </Button>
      </Flex>
    </Paper>
  );
}

export default ActivityAssignments;
